import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { auditsApi } from '../services/audits';
import type { Audit, WebsiteAgentType, AgentStatus } from '@traaker/shared';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  running: 'En cours',
  completed: 'Termine',
  failed: 'Echoue',
};

const AGENT_LABELS: Record<WebsiteAgentType, string> = {
  crawl: 'Crawl',
  content_freshness: 'Fraicheur',
  structural_seo: 'SEO Structurel',
  content_quality: 'Qualite Contenu',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const AGENT_KEYS: WebsiteAgentType[] = ['crawl', 'content_freshness', 'structural_seo', 'content_quality'];

export default function Audits() {
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const navigate = useNavigate();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const brandDomain = activeBrand?.url
    ? (() => { try { return new URL(activeBrand.url).hostname; } catch { return activeBrand.url; } })()
    : tenant?.domain;

  const loadAudits = async () => {
    try {
      const data = await auditsApi.list('website', activeBrand?.brandId);
      setAudits(data);
    } catch {
      setError('Erreur lors du chargement des audits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAudits();
  }, [activeBrand?.brandId]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create('website', {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Audit Website
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Nouvel Audit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucun audit pour le moment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Lancez votre premier audit pour analyser le contenu de {brandDomain || 'votre site'} et
              son optimisation pour les moteurs IA.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Lancer un audit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Domaine</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Agents</TableCell>
                <TableCell>Score Website</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => (
                <TableRow
                  key={audit.auditId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/audits/${audit.auditId}`)}
                >
                  <TableCell>
                    {new Date(audit.createdAt).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>
                    {audit.domain || brandDomain || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[audit.status] || audit.status}
                      color={STATUS_COLORS[audit.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {AGENT_KEYS.map((agent) => {
                        const status = audit.agentStatuses?.[agent] || 'pending';
                        return (
                          <Tooltip key={agent} title={`${AGENT_LABELS[agent]}: ${STATUS_LABELS[status] || status}`}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: AGENT_STATUS_COLORS[status] || '#555',
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {audit.websiteSummary?.overallGeoScore ?? '—'}
                      {audit.websiteSummary?.overallGeoScore !== undefined && '/100'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel Audit Website</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              Lancer un audit complet de <strong>{brandDomain || activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              L'audit analysera la fraicheur du contenu, le SEO structurel et la qualite
              de contenu GEO de votre site.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Lancer l'audit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
